import { defineComponent as _defineComponent } from 'vue'
import { apiPost } from '@/store/api';
import { getDeviceIdFromCookie } from '@/store/cookieStorage';
import { computed, ref, watch } from 'vue';
import { useApplicationStore } from '@/store/applicationStore';
import { useGatComponentsStore } from '@/store/gatComponentsStore';
import { useDocumentUploaderReplaceFilesDialog } from './composables/useDocumentUploaderReplaceFilesDialog';


export default /*#__PURE__*/_defineComponent({
  __name: 'DocumentUploader',
  props: {
    parentId: null,
    location: null,
    disabled: { type: Boolean },
    autoUpload: { type: Boolean },
    documents: null,
    droppedFiles: null
  },
  emits: ["file-selected", "get-documents"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'file-selected', payload: any[]): void;
  (e: 'get-documents', parentId: string | number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  parentId: string | number;
  location: string;
  disabled?: boolean;
  autoUpload?: boolean;
  documents: any[];
  droppedFiles: any[];
};





const applicationStore = useApplicationStore();

const gatComponentsStore = useGatComponentsStore();

const filesToUpload = ref<any[]>(props.droppedFiles || []);
const autoUpload = ref<boolean>(props.autoUpload || false);

const {
  showReplaceDocumentDialog,
  replaceDialogAnswer,
  checkForReplacableFiles,
  replaceDialogContent,
  getFirstReplaceFileId,
} = useDocumentUploaderReplaceFilesDialog(props, filesToUpload);

watch(
  filesToUpload.value,
  (selectedFiles) => {
    const files: any[] = [];
    if (selectedFiles) {
      selectedFiles.forEach((file) => {
        if (file && file.name) {
          files.push({ name: file.name });
        }
      });
    }
    emit('file-selected', files);
  },
  {
    immediate: true,
  }
);

const uploading = ref(false);

const rules = computed(() => [
  (files: any) => {
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      if (file.size > 10000000) {
        return 'File size must be less than 10 MB!';
      }
    }

    return true;
  },
  (files: any) => {
    if (files.length > 9) {
      return 'Max 10 files can be uploaded at once';
    }

    return true;
  },
]);

const rulesAreValid = computed(() => {
  const result = true;
  for (let index = 0; index < rules.value.length; index++) {
    const rule = rules.value[index];
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const result = rule(filesToUpload.value);
    if (result !== true) {
      return false;
    }
  }
  return result;
});

function getDocuments(parentId: string | number) {
  emit('get-documents', parentId);
}

async function uploadFiles() {
  const answer = await checkForReplacableFiles();
  if (answer === 'cancel') {
    filesToUpload.value = [];
    return;
  }
  try {
    uploading.value = true;
    const formData = new FormData();
    formData.append('location', props.location);
    formData.append('parentId', props.parentId.toString());
    for (let index = 0; index < filesToUpload.value.length; index++) {
      const file = filesToUpload.value[index];

      // https://developer.mozilla.org/en-US/docs/Web/API/File/lastModified
      let modifiedDate: string | Date = new Date(file.lastModified);
      if (!modifiedDate) {
        // deprecated / fallback.
        // https://developer.mozilla.org/en-US/docs/Web/API/File/lastModifiedDate
        modifiedDate = file.lastModifiedDate;
      }
      modifiedDate = modifiedDate.toString().substring(0, 21);
      formData.append(`lastModified_${index}`, modifiedDate);
      formData.append(`file_${index}`, file);
      formData.append(`replace_file_${index}_ids`, getFirstReplaceFileId(filesToUpload.value[index].name));
    }
    const headers = {
      'Content-Type': 'multipart/form-data',
      gstoken: applicationStore.user.apiToken,
      gsdevice: getDeviceIdFromCookie(),
    };
    apiPost('document/uploadfiles', formData, { headers })
      .then((response) => {
        if (response.resultCategory == 1) {
          filesToUpload.value = [];
          getDocuments(props.parentId);
        } else {
          getDocuments(props.parentId);
          filesToUpload.value = filesToUpload.value.filter((file) => response.payload.indexOf(file.name) >= 0);
        }
      })
      .finally(() => {
        uploading.value = false;
      });
  } catch (err) {
    window.messageBus.$emit('showMessage', { type: 'error', text: 'An error occured', subText: err, icon: true });
    uploading.value = false;
  }
}

watch(
  autoUpload,
  (newValue) => {
    if (newValue && filesToUpload.value.length > 0) {
      uploadFiles();
    }
  },
  {
    immediate: true,
  }
);

return { __sfc: true,props, emit, applicationStore, gatComponentsStore, filesToUpload, autoUpload, showReplaceDocumentDialog, replaceDialogAnswer, checkForReplacableFiles, replaceDialogContent, getFirstReplaceFileId, uploading, rules, rulesAreValid, getDocuments, uploadFiles }
}

})